// Utility resolusi logo perusahaan dengan fallback ke logo RadFast Billing.
// Dipakai di LoginPage, Sidebar, MitraLayout, halaman voucher/invoice — agar
// branding konsisten saat admin belum mengupload logo kustom.

import api from "./api";
import defaultLogoUrl from "../assets/images/logo.svg";

/**
 * Logo SVG bawaan RadFast Billing (di-bundle ke dalam build Vue).
 *
 * Dipakai sebagai fallback otomatis di `fetchAppBranding()` dan
 * `resolveBrandLogoWithFallback()` ketika admin belum mengupload
 * `company_logo`. Berkas SVG ini ringan (<5KB) dan tidak memerlukan
 * request jaringan tambahan.
 */
export const RADFASTBILL_LOGO = defaultLogoUrl as string;

/** Nama brand default ketika backend tidak mengembalikan apapun. */
export const RADFASTBILL_BRAND_NAME = "RadFast Billing";

/**
 * Mengubah path logo (relatif atau absolut) menjadi URL siap pakai.
 *
 * - URL absolut (`http(s)://`) atau data URI dikembalikan apa adanya.
 * - URL relatif (mis. `/uploads/logo.png`) di-prefix dengan origin server API.
 * - String kosong / `null` / `undefined` mengembalikan string kosong, supaya
 *   komponen pemanggil bisa memutuskan menampilkan fallback teks/ikon.
 *
 * Lihat `resolveBrandLogoWithFallback` kalau memang butuh URL gambar
 * yang dijamin tidak kosong (akan jatuh ke `RADFASTBILL_LOGO`).
 */
export function resolveBrandLogo(raw?: string | null): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;

  const baseURL = String(api.defaults.baseURL ?? "http://localhost:8081/api");
  const origin = baseURL.replace(/\/api\/?$/, "");
  if (value.startsWith("/")) return origin + value;
  return `${origin}/${value}`;
}

/**
 * Sama seperti `resolveBrandLogo`, tapi kalau hasilnya kosong akan
 * jatuh ke `RADFASTBILL_LOGO` (SVG bundled). Pakai ini di komponen
 * yang memang butuh `<img>` dengan src valid (mis. invoice PDF, halaman
 * pembayaran Tripay/Midtrans, header email).
 */
export function resolveBrandLogoWithFallback(raw?: string | null): string {
  const resolved = resolveBrandLogo(raw);
  return resolved || RADFASTBILL_LOGO;
}

/**
 * Menangani gambar logo yang gagal dimuat browser (404/host mati).
 * Mengganti `src` ke logo bawaan hanya satu kali agar error aset fallback
 * tidak memicu loop handler yang sama.
 */
export function applyBrandLogoFallback(event: Event): void {
  const target = event.target as HTMLImageElement | null;
  if (!target || target.src === RADFASTBILL_LOGO) return;
  target.src = RADFASTBILL_LOGO;
}

/**
 * Helper untuk komponen yang memuat info brand dari endpoint `/app-info`.
 *
 * Mengembalikan `{ name, logo, version }`. Bila endpoint gagal atau tidak
 * mengembalikan logo, `logo` di-default ke `RADFASTBILL_LOGO` (SVG bundled)
 * supaya UI tetap punya gambar untuk ditampilkan tanpa request tambahan.
 * Komponen yang ingin perilaku lama (string kosong → fallback teks) bisa
 * memanggil `resolveBrandLogo` langsung dari `data.company_logo`.
 */
export async function fetchAppBranding(): Promise<{ name: string; logo: string; version: string }> {
  try {
    const res = await api.get("/app-info");
    const data = res.data?.data ?? {};
    const name = String(data.company_name || "").trim();
    return {
      name: name || RADFASTBILL_BRAND_NAME,
      logo: resolveBrandLogoWithFallback(data.company_logo),
      version: String(res.data?.version || ""),
    };
  } catch {
    return { name: RADFASTBILL_BRAND_NAME, logo: RADFASTBILL_LOGO, version: "" };
  }
}
